import moment from "moment";
import { STORYDOC_VARIABLES } from "../data/storydoc-variables";
import {
  DEFAULT_DATE_FORMAT,
  defaultDateFormatFor,
  parseDateToken,
  type DateOffset,
} from "./date-token";

const systemKeysIn = (category: string) =>
  STORYDOC_VARIABLES.filter(
    (variable) => variable.type === "system" && variable.category === category,
  ).map((variable) => variable.key);

// Built-in Time/Date variables (catalogue type "system"): the share dialog
// pre-selects today for them and their base value is the render/create "now"
// unless the sender picked another day. `current.date` is the dotted form
// seen in designs.
const NOW_BASED_NAMES = new Set(["current.date", ...systemKeysIn("time-date")]);

/** Display/output format a built-in date variable uses when its token has
 * none — `undefined` means the stored `YYYY-MM-DD` shape. */
export const getDateDisplayFormat = (name: string): string | undefined => {
  const format = defaultDateFormatFor(name);
  return format === DEFAULT_DATE_FORMAT ? undefined : format;
};

// Built-in date-typed variables (base = the sender-provided date value) —
// includes the Date Operations (Date +Xd, Date +Xm, Date format): plain date
// pickers with nothing pre-selected; the pill's offset/format applies to the
// sender-picked day, falling back to "now" when none was picked.
const STORYDOC_DATE_KEYS = new Set(
  STORYDOC_VARIABLES.filter((variable) => variable.type === "date").map(
    (variable) => variable.key,
  ),
);

export const isNowBasedDateName = (name: string): boolean =>
  NOW_BASED_NAMES.has(name);

/**
 * Whether a bare variable name is a date variable eligible for the date
 * popover. `extraDateNames` lets callers add custom (user-created) date
 * variables discovered at runtime.
 */
export const isDateVariableName = (
  name: string,
  extraDateNames?: ReadonlySet<string>,
): boolean =>
  NOW_BASED_NAMES.has(name) ||
  STORYDOC_DATE_KEYS.has(name) ||
  (extraDateNames?.has(name) ?? false);

const UNIT_TO_MOMENT: Record<
  DateOffset["unit"],
  moment.unitOfTime.DurationConstructor
> = {
  d: "days",
  m: "months",
  y: "years",
};

/**
 * Resolves a date token (`name[±Nd|m|y][|format]`) to its formatted string.
 * `baseValue` is the sender-provided date; it wins whenever it is a valid
 * date, for now-based names too (the share dialog pre-selects today in a
 * calendar, and a sender may pick another day). Missing/invalid → `now`.
 */
export const resolveDateToken = (
  raw: string,
  { now, baseValue }: { now: Date; baseValue?: unknown },
): string => {
  const { name, offset, format } = parseDateToken(raw);

  let base =
    baseValue != null && baseValue !== ""
      ? moment(String(baseValue))
      : moment(now);
  if (!base.isValid()) base = moment(now);

  if (offset && offset.amount) {
    base = base.add(offset.amount, UNIT_TO_MOMENT[offset.unit]);
  }
  return base.format(format || defaultDateFormatFor(name));
};
